<template>
  <v-container class="lx-page">
    <header class="lx-page-head">
      <span class="kicker">Data · WDB</span>
      <h1>Weekly Database</h1>
      <h4>
        Weekly macroeconomic and financial indicators for 15 emerging markets
        plus the international drivers of EM asset prices, from 2010. Market
        data are weekly averages; low-frequency macro data are interpolated.
        <a href="#" @click.prevent="dialog = true">How to use this tool</a>
      </h4>
    </header>

    <v-dialog v-model="dialog" width="560" scrollable>
      <v-card>
        <div class="lx-panel__head">
          <span class="lx-panel__title">About the Weekly Database</span>
        </div>
        <v-card-text class="lx-panel__body">
          <p>
            Pick a country and a variable to plot its full weekly history, then
            use <b>Download</b> to save the chart as an image or the series as
            an Excel file. Each observation is either a weekly average of
            high-frequency market data or an interpolation of low-frequency
            macroeconomic data starting in 2010.
          </p>
          <p class="mb-0">
            H2F2 is a proprietary product of Janus4cast LLC for which a USPTO
            patent is pending. Reproducing graphical and/or numerical results
            obtained with this tool requires citing www.Janus4cast.com as the
            primary source.
          </p>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="lx-toolbar">
      <label class="lx-field">
        <span class="lx-field__label">Country</span>
        <select class="form-control minimal" v-model="value" @change="onChange">
          <option
            v-for="item in getcontriesConsensusFilter"
            :value="item"
            :key="item.name"
          >
            {{ item.name }}
          </option>
        </select>
      </label>
      <label class="lx-field lx-field--wide">
        <span class="lx-field__label">Variable</span>
        <select class="form-control minimal" v-model="value2" @change="onChange">
          <option
            v-for="item in getVariableVisualization"
            :value="item"
            :key="item.name"
          >
            {{ item.name }}
          </option>
        </select>
      </label>
      <span class="lx-toolbar__spacer"></span>
      <v-menu>
        <template v-slot:activator="{ props }">
          <button class="lx-btn" v-bind="props" :disabled="!points.length">
            Download
          </button>
        </template>
        <v-list density="compact">
          <v-list-item v-for="(item, index) in menu" :key="index" @click="download(index)">
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <section class="lx-panel">
      <div class="lx-panel__head">
        <span class="lx-panel__title">{{ chartTitle }}</span>
        <span class="lx-panel__meta" v-if="points.length">
          Weekly · {{ rangeLabel }}
        </span>
      </div>
      <div class="lx-kpi-strip" v-if="points.length">
        <div class="lx-kpi">
          <span class="lx-kpi__label">Latest · {{ latestDate }}</span>
          <span class="lx-kpi__value lx-kpi__value--sm">{{ latestValue }}</span>
        </div>
        <div class="lx-kpi" v-for="c in changes" :key="c.label">
          <span class="lx-kpi__label">{{ c.label }}</span>
          <span class="lx-delta" :class="c.cls">{{ c.text }}</span>
        </div>
        <div class="lx-kpi">
          <span class="lx-kpi__label">Range since 2010</span>
          <span class="lx-kpi__sub">{{ rangeMinMax }}</span>
        </div>
      </div>
      <div class="lx-chart__frame">
        <div
          ref="container"
          class="lx-chart"
          style="height: 440px"
          role="img"
          :aria-label="chartTitle"
        ></div>
      </div>
    </section>

    <p class="lx-footnote">Dataset updated {{ dateFormat }}.</p>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import {
  createTimeChart,
  setTimeDomain,
  setValueDomain,
  addLine,
  toPoints,
  fmtNumber,
  fmtDate,
  css,
  disposeChart,
} from "@/plugins/lxChart";

export default {
  name: "historical-data-visualization",
  data() {
    return {
      value: {},
      value2: {},
      chart: null,
      menu: [{ title: "Chart image (PNG)" }, { title: "Series data (Excel)" }],
      dialog: false,
    };
  },
  computed: {
    ...mapGetters([
      "getcontriesVizualization",
      "getdataVizualization",
      "getVariableVisualization",
      "getDateUpdate",
    ]),
    dateFormat() {
      return this.getDateUpdate
        ? moment(this.getDateUpdate).format("D MMM YYYY")
        : "–";
    },
    points() {
      try {
        return toPoints(JSON.parse(this.getdataVizualization || "{}"));
      } catch (e) {
        return [];
      }
    },
    chartTitle() {
      if (!this.value.name || !this.value2.name) return "Select a series";
      return `${this.value.name} · ${this.value2.name}`;
    },
    rangeLabel() {
      const p = this.points;
      return `${fmtDate(p[0][0], "MMM yyyy")} – ${fmtDate(p[p.length - 1][0], "MMM yyyy")}`;
    },
    latestDate() {
      const p = this.points;
      return fmtDate(p[p.length - 1][0], "dd MMM yyyy");
    },
    latestValue() {
      const p = this.points;
      return fmtNumber(p[p.length - 1][1]);
    },
    rangeMinMax() {
      const vs = this.points.map((p) => p[1]);
      return `${fmtNumber(Math.min(...vs))} – ${fmtNumber(Math.max(...vs))}`;
    },
    changes() {
      const p = this.points;
      const last = p[p.length - 1];
      const out = [];
      for (const [label, weeks] of [["4-week change", 4], ["52-week change", 52]]) {
        const prev = p[p.length - 1 - weeks];
        if (!prev || !prev[1]) continue;
        const pct = (last[1] / prev[1] - 1) * 100;
        out.push({
          label,
          text: `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`,
          cls: Math.abs(pct) < 0.05 ? "lx-delta--flat" : pct > 0 ? "lx-delta--up" : "lx-delta--down",
        });
      }
      return out;
    },
    getcontriesConsensusFilter() {
      return this.getcontriesVizualization.filter(
        (i) => i.dataVisualizationFilter === true
      );
    },
  },
  methods: {
    ...mapActions([
      "setCountriesVisualization",
      "setDataVisualization",
      "setVariableVisualizatoin",
      "setDateUpdate",
    ]),
    draw() {
      if (!this.$refs.container) return;
      if (!this.chart) {
        this.chart = createTimeChart(this.$refs.container);
        this.chart.draw();
      }
      const chart = this.chart;
      chart.removeAllSeries();
      const p = this.points;
      if (!p.length) return;
      setTimeDomain(chart, p[0][0], p[p.length - 1][0]);
      setValueDomain(chart, p.map((x) => x[1]));
      addLine(chart, p, {
        name: this.value2.name || "Value",
        color: css("--blue", "#2563b8"),
        width: 1.4,
      });
    },
    onChange() {
      if (this.value.dataVisualization && this.value2.dataVisualization) {
        this.setDataVisualization([
          this.value.dataVisualization,
          this.value2.dataVisualization,
        ]);
      }
    },
    download(index) {
      if (!this.chart) return;
      const name = `Janus4cast WDB - ${this.value.name} - ${this.value2.name}`;
      if (index === 0) {
        this.chart.saveAsPng({ width: 1400, height: 600, quality: 1, filename: name });
      } else {
        this.chart.saveAsXlsx("raw", name);
      }
    },
  },
  watch: {
    getcontriesVizualization(val) {
      if (val && val.length) {
        this.value = this.getcontriesConsensusFilter[0] || val[0];
      }
    },
    getVariableVisualization(val) {
      if (val && val.length) {
        this.value2 = val[0];
        this.onChange();
      }
    },
    getdataVizualization() {
      this.$nextTick(() => this.draw());
    },
    value(val) {
      if (val && val.id !== undefined) this.setVariableVisualizatoin(val.id);
    },
  },
  mounted() {
    this.setDateUpdate();
    // the country list may already be in the store from another page, in which
    // case its watcher never fires: pick the default here too
    if (this.getcontriesConsensusFilter.length) {
      this.value = this.getcontriesConsensusFilter[0];
    }
    this.setCountriesVisualization();
    this.draw();
  },
  beforeUnmount() {
    disposeChart(this.chart);
    this.chart = null;
  },
};
</script>
